import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { Calendar, CreditCard, Mail, CheckCircle, XCircle, AlertCircle, Clock } from 'lucide-react';
import observabilityService from '../../services/observabilityService';

const StatCard = ({ label, value, icon: Icon, color, loading }) => {
  const colorMap = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
    green: { bg: 'bg-green-100', text: 'text-green-600' },
    yellow: { bg: 'bg-yellow-100', text: 'text-yellow-600' },
    red: { bg: 'bg-red-100', text: 'text-red-600' },
  };
  const colors = colorMap[color] || colorMap.blue;

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-full ${colors.bg}`}>
          <Icon className={`h-6 w-6 ${colors.text}`} />
        </div>
      </div>
      {loading ? (
        <div className="space-y-2">
          <div className="h-8 bg-gray-200 rounded animate-pulse w-16" />
          <div className="h-4 bg-gray-200 rounded animate-pulse w-24" />
        </div>
      ) : (
        <>
          <p className="text-3xl font-bold text-gray-900">
            {value != null ? (typeof value === 'number' ? value.toLocaleString() : value) : '--'}
          </p>
          <p className="text-sm text-gray-500 mt-1">{label}</p>
        </>
      )}
    </div>
  );
};

const SectionError = ({ message }) => (
  <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
    <p className="text-sm text-red-600">{message}</p>
  </div>
);

const LoadingSkeleton = ({ rows = 5 }) => (
  <div className="space-y-3">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-6 bg-gray-200 rounded animate-pulse" />
    ))}
  </div>
);

const formatTimestamp = (ts) => {
  if (!ts) return '--';
  try {
    return format(parseISO(ts), 'MMM d, yyyy h:mm a');
  } catch {
    return ts;
  }
};

const formatRelativeTime = (ts) => {
  if (!ts) return '--';
  try {
    return formatDistanceToNow(parseISO(ts), { addSuffix: true });
  } catch {
    return ts;
  }
};

const getCalendarStatusBadge = (provider) => {
  if (!provider || !provider.enabled) {
    return { label: 'Disabled', classes: 'bg-gray-100 text-gray-600' };
  }
  if (provider.sync_errors > 0) {
    return { label: `${provider.sync_errors} errors`, classes: 'bg-red-100 text-red-700' };
  }
  if (provider.last_sync) {
    const lastSync = parseISO(provider.last_sync);
    const hoursSinceSync = (Date.now() - lastSync.getTime()) / (1000 * 60 * 60);
    if (hoursSinceSync > 24) {
      return { label: 'Stale', classes: 'bg-yellow-100 text-yellow-700' };
    }
    return { label: 'Synced', classes: 'bg-green-100 text-green-700' };
  }
  return { label: 'No sync', classes: 'bg-gray-100 text-gray-600' };
};

const getMostRecentSync = (google, outlook) => {
  const gTime = google?.last_sync ? parseISO(google.last_sync).getTime() : 0;
  const oTime = outlook?.last_sync ? parseISO(outlook.last_sync).getTime() : 0;
  if (gTime === 0 && oTime === 0) return null;
  return gTime > oTime ? google.last_sync : outlook.last_sync;
};

const getSuccessRateColor = (rate) => {
  if (rate == null) return 'text-gray-500';
  if (rate >= 95) return 'text-green-600';
  if (rate >= 90) return 'text-yellow-600';
  return 'text-red-600';
};

const IntegrationsTab = ({ selectedFirmId }) => {
  // Calendar state
  const [calendarData, setCalendarData] = useState(null);
  const [calendarPage, setCalendarPage] = useState(1);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [calendarError, setCalendarError] = useState(null);
  const [showFailures, setShowFailures] = useState(true);

  // LawPay state
  const [lawpayData, setLawpayData] = useState(null);
  const [lawpayLoading, setLawpayLoading] = useState(true);
  const [lawpayError, setLawpayError] = useState(null);

  // Communications state
  const [commsData, setCommsData] = useState(null);
  const [commsLoading, setCommsLoading] = useState(true);
  const [commsError, setCommsError] = useState(null);

  // Fetch lawpay + comms on firmId change (non-paginated)
  const fetchCoreData = useCallback(async () => {
    setLawpayLoading(true);
    setLawpayError(null);
    setCommsLoading(true);
    setCommsError(null);

    const results = await Promise.allSettled([
      observabilityService.getLawpayStatus(selectedFirmId),
      observabilityService.getCommunicationRates(selectedFirmId),
    ]);

    if (results[0].status === 'fulfilled') {
      setLawpayData(results[0].value);
    } else {
      console.error('Error fetching LawPay status:', results[0].reason);
      setLawpayError('Failed to load LawPay integration data');
    }
    setLawpayLoading(false);

    if (results[1].status === 'fulfilled') {
      setCommsData(results[1].value);
    } else {
      console.error('Error fetching communication rates:', results[1].reason);
      setCommsError('Failed to load communication delivery data');
    }
    setCommsLoading(false);
  }, [selectedFirmId]);

  useEffect(() => {
    fetchCoreData();
  }, [fetchCoreData]);

  // Fetch calendar (depends on page and firmId)
  useEffect(() => {
    let cancelled = false;
    const fetchCalendar = async () => {
      setCalendarLoading(true);
      setCalendarError(null);
      try {
        const data = await observabilityService.getCalendarHealth(selectedFirmId, calendarPage, 50);
        if (!cancelled) setCalendarData(data);
      } catch (err) {
        console.error('Error fetching calendar health:', err);
        if (!cancelled) setCalendarError('Failed to load calendar sync data');
      } finally {
        if (!cancelled) setCalendarLoading(false);
      }
    };
    fetchCalendar();
    return () => { cancelled = true; };
  }, [selectedFirmId, calendarPage]);

  // Reset calendar page when firm changes
  useEffect(() => {
    setCalendarPage(1);
  }, [selectedFirmId]);

  // Compute calendar summary stats
  const calendarSummary = (() => {
    if (!calendarData?.users) return null;
    const users = calendarData.users;
    const withCalendar = users.filter(u =>
      (u.google?.enabled) || (u.outlook?.enabled)
    ).length;
    const withErrors = users.filter(u =>
      (u.google?.sync_errors > 0) || (u.outlook?.sync_errors > 0)
    ).length;
    const withStale = users.filter(u => {
      const checkStale = (p) => {
        if (!p?.enabled || !p?.last_sync) return false;
        const hours = (Date.now() - parseISO(p.last_sync).getTime()) / (1000 * 60 * 60);
        return hours > 24;
      };
      return checkStale(u.google) || checkStale(u.outlook);
    }).length;
    return { withCalendar, withErrors, withStale };
  })();

  const calendarTotalPages = calendarData ? calendarData.total_pages || Math.ceil((calendarData.total || 0) / 50) : 0;

  return (
    <div className="space-y-6">
      {/* Section 1: Calendar Sync Health */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center mb-4">
          <Calendar className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Calendar Sync Health</h3>
        </div>
        {calendarError ? (
          <SectionError message={calendarError} />
        ) : calendarLoading ? (
          <LoadingSkeleton rows={6} />
        ) : (
          <>
            {/* Summary stats */}
            {calendarSummary && (
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
                  <CheckCircle className="h-4 w-4 text-blue-600" />
                  <span className="text-sm text-blue-700 font-medium">{calendarSummary.withCalendar} users with calendar enabled</span>
                </div>
                {calendarSummary.withErrors > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg">
                    <XCircle className="h-4 w-4 text-red-600" />
                    <span className="text-sm text-red-700 font-medium">{calendarSummary.withErrors} users with sync errors</span>
                  </div>
                )}
                {calendarSummary.withStale > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 rounded-lg">
                    <Clock className="h-4 w-4 text-yellow-600" />
                    <span className="text-sm text-yellow-700 font-medium">{calendarSummary.withStale} users with stale sync</span>
                  </div>
                )}
              </div>
            )}

            {/* Users table */}
            {calendarData?.users?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Google Calendar</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outlook Calendar</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Sync</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {calendarData.users.map((user, idx) => {
                      const googleStatus = getCalendarStatusBadge(user.google);
                      const outlookStatus = getCalendarStatusBadge(user.outlook);
                      const lastSync = getMostRecentSync(user.google, user.outlook);
                      return (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm whitespace-nowrap">
                            <div className="text-gray-900 font-medium">{user.name}</div>
                            <div className="text-gray-500 text-xs">{user.email}</div>
                          </td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${googleStatus.classes}`}>
                              {googleStatus.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${outlookStatus.classes}`}>
                              {outlookStatus.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                            {lastSync ? formatRelativeTime(lastSync) : '--'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No users with calendar integration found</p>
            )}

            {/* Pagination */}
            {calendarTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <button
                  onClick={() => setCalendarPage((p) => Math.max(1, p - 1))}
                  disabled={calendarPage <= 1}
                  className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {calendarPage} of {calendarTotalPages}
                </span>
                <button
                  onClick={() => setCalendarPage((p) => Math.min(calendarTotalPages, p + 1))}
                  disabled={calendarPage >= calendarTotalPages}
                  className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            )}

            {/* Recent Sync Failures */}
            {calendarData?.recent_failures?.length > 0 && (
              <div className="mt-6">
                <button
                  onClick={() => setShowFailures(!showFailures)}
                  className="flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 mb-3"
                >
                  <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
                  Recent Sync Failures ({calendarData.recent_failures.length})
                  <span className="ml-1 text-gray-400">{showFailures ? '(hide)' : '(show)'}</span>
                </button>
                {showFailures && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Operation</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Error</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {calendarData.recent_failures.map((failure, idx) => (
                          <tr key={idx} className="border-l-4 border-l-red-400 hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                              {formatTimestamp(failure.created_at || failure.sync_time)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                              {failure.user_name || failure.email || '--'}
                            </td>
                            <td className="px-4 py-3 text-sm whitespace-nowrap">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                failure.provider === 'google' ? 'bg-blue-100 text-blue-700' : 'bg-indigo-100 text-indigo-700'
                              }`}>
                                {failure.provider === 'google' ? 'Google' : 'Outlook'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                              {failure.operation || failure.sync_type || '--'}
                            </td>
                            <td className="px-4 py-3 text-sm text-red-600 max-w-xs truncate" title={failure.error_message}>
                              {failure.error_message || '--'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Section 2: LawPay Integration */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center mb-4">
          <CreditCard className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">LawPay Integration</h3>
        </div>
        {lawpayError ? (
          <SectionError message={lawpayError} />
        ) : lawpayLoading ? (
          <LoadingSkeleton rows={5} />
        ) : (
          <>
            {/* Summary stat cards */}
            {lawpayData?.summary && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <StatCard
                  label="Firms Configured"
                  value={lawpayData.summary.total_configured}
                  icon={CreditCard}
                  color="blue"
                  loading={false}
                />
                <StatCard
                  label="Active"
                  value={lawpayData.summary.active_count}
                  icon={CheckCircle}
                  color="green"
                  loading={false}
                />
                <StatCard
                  label="Success Rate"
                  value={lawpayData.summary.overall_success_rate != null ? `${Math.round(lawpayData.summary.overall_success_rate * 10) / 10}%` : '--'}
                  icon={CheckCircle}
                  color={lawpayData.summary.overall_success_rate >= 90 ? 'green' : 'red'}
                  loading={false}
                />
              </div>
            )}

            {/* Firms table */}
            {lawpayData?.firms?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Firm Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Environment</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transactions</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Successful</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Failed</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Success Rate</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Transaction</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {lawpayData.firms.map((firm, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium whitespace-nowrap">{firm.firm_name}</td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            firm.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {firm.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            firm.environment === 'production' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {firm.environment || '--'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{firm.total_transactions ?? '--'}</td>
                        <td className="px-4 py-3 text-sm text-green-600 whitespace-nowrap">{firm.successful ?? '--'}</td>
                        <td className="px-4 py-3 text-sm text-red-600 whitespace-nowrap">{firm.failed ?? '--'}</td>
                        <td className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${getSuccessRateColor(firm.success_rate)}`}>
                          {firm.success_rate != null ? `${Math.round(firm.success_rate * 10) / 10}%` : '--'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {formatTimestamp(firm.last_transaction)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CreditCard className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm">No firms have LawPay configured.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Section 3: Communication Delivery Rates */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center mb-4">
          <Mail className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Communication Delivery Rates</h3>
        </div>
        {commsError ? (
          <SectionError message={commsError} />
        ) : commsLoading ? (
          <LoadingSkeleton rows={4} />
        ) : (
          <>
            {/* Summary */}
            {commsData?.summary && (
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
                  <Mail className="h-4 w-4 text-blue-600" />
                  <span className="text-sm text-blue-700 font-medium">
                    {commsData.summary.total_sent?.toLocaleString() || 0} total sent
                  </span>
                </div>
                {commsData.summary.total_failed > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg">
                    <XCircle className="h-4 w-4 text-red-600" />
                    <span className="text-sm text-red-700 font-medium">
                      {commsData.summary.total_failed?.toLocaleString() || 0} failed
                    </span>
                  </div>
                )}
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                  commsData.summary.overall_success_rate >= 95 ? 'bg-green-50' : commsData.summary.overall_success_rate >= 90 ? 'bg-yellow-50' : 'bg-red-50'
                }`}>
                  <CheckCircle className={`h-4 w-4 ${
                    commsData.summary.overall_success_rate >= 95 ? 'text-green-600' : commsData.summary.overall_success_rate >= 90 ? 'text-yellow-600' : 'text-red-600'
                  }`} />
                  <span className={`text-sm font-medium ${
                    commsData.summary.overall_success_rate >= 95 ? 'text-green-700' : commsData.summary.overall_success_rate >= 90 ? 'text-yellow-700' : 'text-red-700'
                  }`}>
                    {commsData.summary.overall_success_rate != null ? `${Math.round(commsData.summary.overall_success_rate * 10) / 10}%` : '--'} success rate
                  </span>
                </div>
              </div>
            )}

            {/* Rates table */}
            {commsData?.rates?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Delivered</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Failed</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Read</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Success Rate</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {commsData.rates.map((rate, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 capitalize">
                            {rate.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium whitespace-nowrap">{rate.total?.toLocaleString() || 0}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{rate.sent?.toLocaleString() || 0}</td>
                        <td className="px-4 py-3 text-sm text-green-600 whitespace-nowrap">{rate.delivered?.toLocaleString() || 0}</td>
                        <td className="px-4 py-3 text-sm text-red-600 whitespace-nowrap">{rate.failed?.toLocaleString() || 0}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{rate.read_count?.toLocaleString() || 0}</td>
                        <td className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${getSuccessRateColor(rate.success_rate)}`}>
                          {rate.success_rate != null ? `${Math.round(rate.success_rate * 10) / 10}%` : '--'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No communication data available</p>
            )}

            <p className="mt-3 text-xs text-gray-400">
              Email delivery status reflects send success, not inbox delivery. SMTP is fire-and-forget.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default IntegrationsTab;
